import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from './api';
import { isDevMode, getCurrentUser, getUserProjectIds } from './auth';

const ProjectContext = createContext(null);

// Safe hook that works in both dev mode (no MsalProvider) and production
function useIsAuthenticatedSafe() {
  if (isDevMode) return true;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { useIsAuthenticated } = require('@azure/msal-react');
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useIsAuthenticated();
  } catch {
    return false;
  }
}

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const loadProjects = async () => {
    try {
      setError(null);
      const response = await api.get('/api/projects/');
      let data = response.data || [];

      // Scope members to their assigned projects (owners/admins see all)
      const user = getCurrentUser();
      const isRestricted = user?.role === 'member';
      const scopedIds = getUserProjectIds();
      if (isRestricted && scopedIds.length > 0) {
        data = data.filter(p => scopedIds.includes(p._id));
      }

      setProjects(data);
      const savedId = sessionStorage.getItem('heyday_active_project');
      const saved = data.find(p => p._id === savedId);
      setActiveProject(saved || data[0] || null);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(err.message || 'Failed to load projects');
      setProjects([]);
      setActiveProject(null);
    } finally {
      setLoading(false);
    }
  };

  // Load on mount if already authenticated (e.g. page refresh with valid token)
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadProjects();
  }, []);

  // Reload projects when user logs in (client-side navigation — no remount)
  useEffect(() => {
    const handleLogin = () => {
      hasLoadedRef.current = false;
      setLoading(true);
      loadProjects();
    };
    window.addEventListener('heyday:login', handleLogin);
    return () => window.removeEventListener('heyday:login', handleLogin);
  }, []);

  const switchProject = (project) => {
    setActiveProject(project);
    sessionStorage.setItem('heyday_active_project', project._id);
  };

  const refreshProjects = () => {
    hasLoadedRef.current = false;
    setLoading(true);
    loadProjects();
  };

  return (
    <ProjectContext.Provider
      value={{ projects, activeProject, switchProject, loading, refreshProjects, error }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
